import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Heart, TrendingUp, Check, Copy, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { resolveImgUrl } from '@/utils/imgUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = { nama_donatur: '', nominal: '', keterangan: '' };

export default function DonasiPage() {
  const [programs, setPrograms] = useState([]);
  const [donasi, setDonasi] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedProgram, setSelectedProgram] = useState(null); // program object or null
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = () => {
    Promise.all([
      axios.get(`${API}/program-donasi`).then(r => setPrograms(r.data.filter(p => p.is_active))).catch(() => {}),
      axios.get(`${API}/donasi`).then(r => setDonasi(r.data)).catch(() => {}),
      axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {}),
    ]);
  };

  const total = donasi.reduce((sum, d) => sum + (d.nominal || 0), 0);
  const rupiah = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n || 0);

  const openDonateForm = (program) => {
    setSelectedProgram(program);
    setForm(emptyForm);
    setSuccess(false);
    setShowForm(true);
  };

  const openGeneralForm = () => {
    setSelectedProgram(null);
    setForm(emptyForm);
    setSuccess(false);
    setShowForm(true);
  };

  const copyRek = () => {
    navigator.clipboard.writeText(settings.rekening_bank || '');
    toast.success('Nomor rekening disalin!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nama_donatur || !form.nominal) {
      toast.error('Nama dan nominal wajib diisi'); return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        nominal: parseInt(form.nominal),
        program_id: selectedProgram?.id || null,
      };
      await axios.post(`${API}/donasi`, payload);
      toast.success('Terima kasih atas donasinya!');
      setSuccess(true);
      const [pd, d] = await Promise.all([
        axios.get(`${API}/program-donasi`).then(r => r.data.filter(p => p.is_active)),
        axios.get(`${API}/donasi`).then(r => r.data),
      ]);
      setPrograms(pd);
      setDonasi(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim donasi');
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 px-6 pt-12 pb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
              <Heart size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-playfair text-xl font-bold text-white">Donasi Pesantren</h1>
              <p className="text-emerald-300 text-xs">Transparansi & amanah</p>
            </div>
          </div>
          <div className="bg-white/10 rounded-2xl p-4 text-center">
            <p className="text-emerald-200 text-xs mb-1">Total Terkumpul</p>
            <p className="text-3xl font-bold text-white" data-testid="total-donasi">{rupiah(total)}</p>
            <p className="text-emerald-300 text-xs mt-1">dari {donasi.length} donatur</p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">

          {/* ── Program Donasi ── */}
          {programs.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 text-sm mb-3 flex items-center gap-2">
                <Heart size={14} className="text-emerald-500" /> Program Donasi
              </h3>
              <div className="space-y-3">
                {programs.map((p, i) => {
                  const pct = Math.min(Math.round(((p.terkumpul || 0) / (p.target || 1)) * 100), 100);
                  const programTotal = donasi.filter(d => d.program_id === p.id).reduce((s, d) => s + (d.nominal || 0), 0);
                  return (
                    <motion.div
                      key={p.id}
                      data-testid={`program-card-${p.id}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      className="bg-white rounded-2xl overflow-hidden shadow-card border border-gray-50"
                    >
                      {p.foto ? (
                        <img src={resolveImgUrl(p.foto)} alt={p.judul} className="w-full h-36 object-cover" />
                      ) : (
                        <div className="w-full h-36 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                          <Heart size={30} className="text-emerald-300" />
                        </div>
                      )}
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-800 text-sm mb-1">{p.judul}</h4>
                        <p className="text-xs text-gray-500 mb-3 leading-relaxed">{p.deskripsi}</p>
                        <div className="mb-3">
                          <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-emerald-700 font-semibold">{rupiah(p.terkumpul || 0)}</span>
                            <span className="text-gray-400">dari {rupiah(p.target)}</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1 text-right">{pct}% tercapai · {donasi.filter(d => d.program_id === p.id).length} donatur</p>
                        </div>
                        <button
                          data-testid={`donate-program-btn-${p.id}`}
                          onClick={() => openDonateForm(p)}
                          className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white py-2.5 rounded-xl text-sm font-semibold transition-all"
                        >
                          <Heart size={14} /> Donasi untuk Program Ini
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Rekening Bank ── */}
          <div className="bg-white rounded-2xl p-5 shadow-card border border-emerald-50">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <div className="w-2 h-4 bg-emerald-400 rounded-full" /> Info Transfer
            </h3>
            <div className="bg-emerald-50 rounded-xl p-4">
              <p className="text-sm font-semibold text-emerald-800">{settings.rekening_bank || 'Hubungi admin untuk info rekening'}</p>
            </div>
            {settings.rekening_bank && (
              <button
                onClick={copyRek}
                data-testid="copy-rekening-btn"
                className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
              >
                <Copy size={13} /> Salin Nomor Rekening
              </button>
            )}
          </div>

          {/* ── Tombol donasi umum ── */}
          <button
            data-testid="open-general-donate-btn"
            onClick={openGeneralForm}
            className="w-full flex items-center justify-between bg-white rounded-2xl p-4 shadow-card border border-emerald-50 hover:bg-emerald-50 active:scale-[0.98] transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Heart size={17} className="text-emerald-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-700">Donasi Umum</p>
                <p className="text-xs text-gray-400">Tidak terikat program tertentu</p>
              </div>
            </div>
            <ChevronRight size={16} className="text-gray-300" />
          </button>

          {/* ── Laporan Donatur ── */}
          <div>
            <h3 className="font-semibold text-gray-700 text-sm mb-3 flex items-center gap-2">
              <TrendingUp size={15} className="text-emerald-500" /> Laporan Donatur
            </h3>
            <div className="space-y-2">
              {donasi.length === 0 && (
                <div className="bg-white rounded-2xl p-6 text-center text-gray-400 text-sm shadow-card">
                  Belum ada donasi tercatat
                </div>
              )}
              {donasi.map((d, i) => {
                const prog = programs.find(p => p.id === d.program_id);
                return (
                  <motion.div
                    key={d.id}
                    data-testid={`donasi-item-${d.id}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="bg-white rounded-2xl p-4 shadow-card flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Heart size={16} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 text-sm truncate">{d.nama_donatur}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {d.tanggal}
                        {prog ? ` · ${prog.judul}` : ''}
                        {d.keterangan ? ` · ${d.keterangan}` : ''}
                      </p>
                    </div>
                    <p className="font-bold text-emerald-700 text-sm flex-shrink-0">{rupiah(d.nominal)}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Modal Form Donasi ── */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={e => e.target === e.currentTarget && setShowForm(false)}
            >
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md shadow-xl flex flex-col max-h-[90vh]"
              >
                {/* Header — tidak scroll */}
                <div className="flex-shrink-0 flex items-center justify-between p-5 border-b border-gray-100">
                  <div>
                    <h2 className="font-playfair text-lg font-bold text-emerald-950">
                      {selectedProgram ? 'Donasi Program' : 'Donasi Umum'}
                    </h2>
                    {selectedProgram && (
                      <p className="text-xs text-emerald-600 mt-0.5 font-medium">{selectedProgram.judul}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowForm(false)}
                    className="p-2 hover:bg-gray-100 rounded-xl"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Konten scroll */}
                <div className="flex-1 overflow-y-auto min-h-0">
                  <div className="p-5">
                    {!success ? (
                      <form id="donasi-form" onSubmit={handleSubmit} data-testid="donasi-form" className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Donatur *</label>
                          <input
                            required
                            data-testid="donasi-nama"
                            value={form.nama_donatur}
                            onChange={e => setForm(f => ({ ...f, nama_donatur: e.target.value }))}
                            placeholder="Nama Anda / Hamba Allah"
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nominal (IDR) *</label>
                          <input
                            required
                            data-testid="donasi-nominal"
                            type="number"
                            min={1000}
                            value={form.nominal}
                            onChange={e => setForm(f => ({ ...f, nominal: e.target.value }))}
                            placeholder="50000"
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Keterangan</label>
                          <input
                            data-testid="donasi-keterangan"
                            value={form.keterangan}
                            onChange={e => setForm(f => ({ ...f, keterangan: e.target.value }))}
                            placeholder="Operasional, beasiswa, dll."
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          />
                        </div>
                      </form>
                    ) : (
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="py-6 text-center"
                      >
                        <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-emerald">
                          <Check size={24} className="text-white" />
                        </div>
                        <h3 className="font-semibold text-emerald-800">Jazakallahu Khairan!</h3>
                        <p className="text-sm text-emerald-600 mt-1">Semoga donasi Anda menjadi amal jariyah</p>
                        <div className="flex gap-2 mt-5">
                          <button
                            onClick={() => { setSuccess(false); setForm(emptyForm); }}
                            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600"
                          >
                            Donasi Lagi
                          </button>
                          <button
                            onClick={() => setShowForm(false)}
                            className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold"
                          >
                            Tutup
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Tombol submit — selalu terlihat di bawah, tidak tertutup nav */}
                {!success && (
                  <div
                    className="flex-shrink-0 px-5 pt-3 bg-white border-t border-gray-100"
                    style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
                  >
                    <button
                      data-testid="submit-donasi-btn"
                      type="submit"
                      form="donasi-form"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white py-3.5 rounded-xl font-semibold shadow-emerald transition-all disabled:opacity-60"
                    >
                      {loading
                        ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <><Check size={16} /> Catat Donasi</>
                      }
                    </button>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
